const DIRECTOR_STORAGE_KEY='toybox-matchmaker-director-v1';
const DEFAULT_TIMEOUT_MS=120_000;
const POLL_INTERVAL_MS=900;
const RATE_LIMIT_RETRIES=5;

function toHttpUrl(value){
  let raw=String(value||'').trim();
  if(!raw)return '';
  if(!/^[a-z]+:\/\//i.test(raw))raw=`https://${raw}`;
  const url=new URL(raw);
  if(url.protocol==='ws:')url.protocol='http:';
  if(url.protocol==='wss:')url.protocol='https:';
  if(url.protocol!=='http:'&&url.protocol!=='https:')throw new Error('La dirección debe usar HTTP, HTTPS, WS o WSS.');
  return url.toString().replace(/\/$/,'');
}

function delay(ms,signal){
  return new Promise((resolve,reject)=>{
    const timer=setTimeout(resolve,ms);
    signal?.addEventListener('abort',()=>{
      clearTimeout(timer);
      reject(new DOMException('Operación cancelada.','AbortError'));
    },{once:true});
  });
}

function parsePayload(text){
  if(!text)return {};
  try{return JSON.parse(text);}
  catch{return {error:text};}
}

function responseError(status,payload,provider='matchmaker'){
  const message=payload?.error?.message
    ||payload?.error
    ||payload?.message
    ||`${provider==='managed'?'Edgegap Matchmaker':'El director'} respondió ${status}.`;
  const error=new Error(String(message));
  error.status=status;
  error.code=payload?.code||'MATCHMAKER_HTTP_ERROR';
  return error;
}

function boolFromEnv(value,fallback=true){
  if(value===undefined||value===null||value==='')return fallback;
  return !['0','false','no','off'].includes(String(value).toLowerCase());
}

export function assignmentServerUrl(assignment,portName,tlsEnabled){
  const ports=assignment?.ports||{};
  const websocketProtocols=['WS','WSS','WEBSOCKET','WEBSOCKET_SECURE','TCP'];
  const port=ports[portName]
    ||Object.values(ports).find((entry)=>websocketProtocols.includes(String(entry?.protocol||'').toUpperCase()))
    ||Object.values(ports)[0];
  if(!port)throw new Error(`Edgegap no devolvió el puerto "${portName}".`);

  const protocol=String(port.protocol||'').toUpperCase();
  if(protocol&&!websocketProtocols.includes(protocol)){
    throw Object.assign(
      new Error(`El puerto "${portName}" usa ${protocol}; el servidor de The Turbo Crown necesita WS con TLS Upgrade.`),
      {code:'EDGEGAP_PORT_PROTOCOL_ERROR'},
    );
  }
  const assignmentTls=typeof port.tls_upgrade==='boolean'
    ?port.tls_upgrade
    :typeof port.tlsUpgrade==='boolean'
      ?port.tlsUpgrade
      :protocol==='WSS'||protocol==='WEBSOCKET_SECURE'
        ?true
        :tlsEnabled;
  const rawLink=String(port.link||'').trim();
  let serverUrl='';
  if(rawLink){
    if(/^wss?:\/\//i.test(rawLink))serverUrl=rawLink.replace(/\/$/,'');
    if(/^https?:\/\//i.test(rawLink)){
      const url=new URL(rawLink);
      url.protocol=url.protocol==='https:'?'wss:':'ws:';
      serverUrl=url.toString().replace(/\/$/,'');
    }
    if(!serverUrl)serverUrl=`${assignmentTls?'wss':'ws'}://${rawLink.replace(/\/$/,'')}`;
  }else{
    const host=String(assignment?.fqdn||assignment?.public_ip||'').trim();
    if(!host)throw new Error('Edgegap no devolvió una dirección para el servidor asignado.');
    const externalPort=Number(port.external);
    const suffix=!assignmentTls&&Number.isFinite(externalPort)?`:${externalPort}`:'';
    serverUrl=`${assignmentTls?'wss':'ws'}://${host}${suffix}`;
  }

  if(serverUrl.startsWith('ws://')&&globalThis.location?.protocol==='https:'){
    throw Object.assign(
      new Error(`El puerto "${portName}" de Edgegap no tiene TLS Upgrade. Activá WS + TLS Upgrade para conectar desde la web.`),
      {code:'EDGEGAP_TLS_REQUIRED'},
    );
  }
  return serverUrl;
}

async function transportRequest(url,{method='GET',token='',body,signal}={}){
  if(globalThis.window?.toyboxDesktop?.matchmakerRequest){
    const result=await globalThis.window.toyboxDesktop.matchmakerRequest({url,method,token,body});
    return {ok:result.ok,status:result.status,text:result.body||''};
  }
  const response=await fetch(url,{
    method,
    signal,
    cache:'no-store',
    headers:{
      Accept:'application/json',
      ...(token?{Authorization:token}:{}),
      ...(body?{'Content-Type':'application/json'}:{}),
    },
    body,
  });
  return {ok:response.ok,status:response.status,text:await response.text()};
}

export class EdgegapMatchmakingClient {
  constructor(options={}){
    this.managedUrl=toHttpUrl(options.managedUrl??import.meta.env?.VITE_EDGEGAP_MATCHMAKER_URL??'');
    this.managedToken=String(options.managedToken??import.meta.env?.VITE_EDGEGAP_MATCHMAKER_TOKEN??'').trim();
    this.managedProfile=String(options.managedProfile??import.meta.env?.VITE_EDGEGAP_MATCHMAKER_PROFILE??'carscape-quick').trim();
    this.managedPortName=String(options.managedPortName??import.meta.env?.VITE_EDGEGAP_MATCHMAKER_PORT??'gameport').trim();
    this.managedTls=boolFromEnv(options.managedTls??import.meta.env?.VITE_EDGEGAP_MATCHMAKER_TLS,true);
    this.explicitDirector=toHttpUrl(options.directorUrl??import.meta.env?.VITE_MATCHMAKER_URL??'');
    this.timeoutMs=Number(options.timeoutMs)||DEFAULT_TIMEOUT_MS;
    this.pollIntervalMs=Number(options.pollIntervalMs)||POLL_INTERVAL_MS;
    this.persistedDirector='';
    try{this.persistedDirector=toHttpUrl(localStorage.getItem(DIRECTOR_STORAGE_KEY)||'');}catch{}
    this.abortController=null;
    this.ticket=null;
    this.provider='';
    this.directorUrl=this.explicitDirector||this.persistedDirector;
  }

  get isManagedConfigured(){
    return Boolean(this.managedUrl&&this.managedToken&&this.managedProfile);
  }

  get hasPartialManagedConfig(){
    return Boolean(this.managedUrl||this.managedToken)&&!this.isManagedConfigured;
  }

  candidate(serverUrl){
    return this.explicitDirector||this.persistedDirector||toHttpUrl(serverUrl);
  }

  async request(url,options={},provider='director'){
    for(let attempt=0;attempt<=RATE_LIMIT_RETRIES;attempt+=1){
      const response=await transportRequest(url,options);
      const payload=parsePayload(response.text);
      if(response.ok)return payload;
      if(response.status!==429||attempt===RATE_LIMIT_RETRIES){
        throw responseError(response.status,payload,provider);
      }
      const backoff=Math.min(8000,500*(2**attempt))+Math.floor(Math.random()*250);
      await delay(backoff,options.signal);
    }
    throw new Error('No se pudo consultar el matchmaking.');
  }

  async availability(serverUrl){
    if(this.isManagedConfigured){
      return {enabled:true,provider:'edgegap-managed',url:this.managedUrl,profile:this.managedProfile};
    }
    if(this.hasPartialManagedConfig){
      return {enabled:false,provider:'edgegap-managed',configurationError:true};
    }
    const director=this.candidate(serverUrl);
    if(!director)return {enabled:false,director:''};
    try{
      const config=await this.request(`${director}/matchmaking/config`);
      return {...config,director,provider:'director'};
    }catch(error){
      if(error.status===404)return {enabled:false,director};
      throw error;
    }
  }

  async findManagedServer({onStatus}){
    const controller=this.abortController;
    this.provider='edgegap-managed';
    onStatus({status:'queued',message:'BUSCANDO JUGADORES EN EDGEGAP…'});
    const created=await this.request(`${this.managedUrl}/tickets`,{
      method:'POST',
      token:this.managedToken,
      body:JSON.stringify({
        player_ip:null,
        profile:this.managedProfile,
        attributes:{},
      }),
      signal:controller.signal,
    },'managed');
    this.ticket={...created,_provider:'edgegap-managed'};
    const deadline=Date.now()+this.timeoutMs;
    let lastStatus='SEARCHING';

    while(Date.now()<deadline){
      if(controller.signal.aborted)throw new DOMException('Operación cancelada.','AbortError');
      const status=String(this.ticket?.status||'SEARCHING').toUpperCase();
      lastStatus=status;
      if(status==='HOST_ASSIGNED'&&this.ticket.assignment){
        const serverUrl=assignmentServerUrl(this.ticket.assignment,this.managedPortName,this.managedTls);
        const ready={
          ...this.ticket,
          status:'ready',
          serverUrl,
          location:this.ticket.assignment.location||null,
          provider:'edgegap-managed',
          releaseAfterConnect:false,
        };
        this.ticket={...this.ticket,_provider:'edgegap-managed'};
        this.abortController=null;
        onStatus({status:'ready',message:'SERVIDOR REGIONAL LISTO',ticket:ready});
        return ready;
      }
      if(['CANCELLED','ERROR','FAILED','EXPIRED'].includes(status)){
        throw Object.assign(new Error(this.ticket?.error||'Edgegap no pudo preparar el servidor regional.'),{code:'MATCHMAKER_FAILED'});
      }
      const messages={
        SEARCHING:'BUSCANDO JUGADORES EN EDGEGAP…',
        MATCH_FOUND:'PARTIDA ENCONTRADA · ASIGNANDO REGIÓN…',
        TEAM_FOUND:'JUGADORES AGRUPADOS · ASIGNANDO REGIÓN…',
      };
      onStatus({status:status.toLowerCase(),message:messages[status]||'PREPARANDO SERVIDOR REGIONAL…',ticket:this.ticket});
      await delay(this.pollIntervalMs,controller.signal);
      const updated=await this.request(`${this.managedUrl}/tickets/${encodeURIComponent(this.ticket.id)}`,{
        token:this.managedToken,
        signal:controller.signal,
      },'managed');
      this.ticket={...updated,_provider:'edgegap-managed'};
    }
    const message=lastStatus==='SEARCHING'
      ?'Edgegap no reunió los jugadores antes de que venciera la búsqueda.'
      :'Edgegap encontró la partida, pero no asignó el servidor regional a tiempo.';
    throw Object.assign(new Error(message),{
      code:'MATCHMAKER_TIMEOUT',
      phase:lastStatus.toLowerCase(),
    });
  }

  async findDirectorServer({serverUrl,mode,mapId,onStatus}){
    const controller=this.abortController;
    const director=this.candidate(serverUrl);
    if(!director)throw Object.assign(new Error('No hay un director configurado.'),{code:'MATCHMAKER_UNAVAILABLE'});
    let config;
    try{config=await this.availability(director);}
    catch(error){
      error.code=error.code||'MATCHMAKER_UNAVAILABLE';
      throw error;
    }
    if(!config.enabled){
      throw Object.assign(new Error('El servidor indicado no tiene asignación automática.'),{code:'MATCHMAKER_UNAVAILABLE'});
    }
    this.provider='director';
    this.directorUrl=director;
    if(!this.explicitDirector){
      this.persistedDirector=director;
      localStorage.setItem(DIRECTOR_STORAGE_KEY,director);
    }
    onStatus({status:'queued',message:'AGRUPANDO JUGADORES…'});
    const created=await this.request(`${director}/matchmaking/tickets`,{
      method:'POST',
      body:JSON.stringify({mode,mapId}),
      signal:controller.signal,
    });
    this.ticket={...created.ticket,_provider:'director'};
    const deadline=Date.now()+this.timeoutMs;
    while(Date.now()<deadline){
      if(controller.signal.aborted)throw new DOMException('Operación cancelada.','AbortError');
      const ticket=this.ticket;
      if(ticket?.status==='ready'&&ticket.serverUrl){
        const ready={...ticket,provider:'director',releaseAfterConnect:true};
        onStatus({status:'ready',message:'SERVIDOR REGIONAL LISTO',ticket:ready});
        this.abortController=null;
        return ready;
      }
      if(ticket?.status==='error'||ticket?.status==='expired'||ticket?.status==='cancelled'){
        throw Object.assign(new Error(ticket.error||'No se pudo preparar el servidor regional.'),{code:'MATCHMAKER_FAILED'});
      }
      onStatus({
        status:ticket?.status||'queued',
        message:ticket?.status==='deploying'?'INICIANDO SERVIDOR CERCANO…':'AGRUPANDO JUGADORES…',
        ticket,
      });
      await delay(this.pollIntervalMs,controller.signal);
      const result=await this.request(`${director}/matchmaking/tickets/${encodeURIComponent(ticket.id)}`,{
        signal:controller.signal,
      });
      this.ticket={...result.ticket,_provider:'director'};
    }
    throw Object.assign(new Error('El servidor regional tardó demasiado en iniciar.'),{code:'MATCHMAKER_TIMEOUT'});
  }

  async findServer({serverUrl,mode,mapId,onStatus=()=>{}}){
    this.cancel();
    this.abortController=new AbortController();
    try{
      if(this.isManagedConfigured)return await this.findManagedServer({onStatus});
      if(this.hasPartialManagedConfig){
        throw Object.assign(new Error('Falta la URL o el token limitado del Matchmaker de Edgegap.'),{code:'MATCHMAKER_CONFIGURATION_ERROR'});
      }
      return await this.findDirectorServer({serverUrl,mode,mapId,onStatus});
    }catch(error){
      if(error.name!=='AbortError')this.cancel();
      throw error;
    }
  }

  releaseAfterConnect(){
    if(this.provider==='director')this.cancel();
  }

  cancel(){
    const ticket=this.ticket;
    const provider=ticket?._provider||this.provider;
    const director=this.directorUrl;
    this.abortController?.abort();
    this.abortController=null;
    this.ticket=null;
    this.provider='';
    if(!ticket?.id)return;
    if(provider==='edgegap-managed'&&this.managedUrl&&this.managedToken){
      void this.request(`${this.managedUrl}/tickets/${encodeURIComponent(ticket.id)}`,{
        method:'DELETE',
        token:this.managedToken,
      },'managed').catch(()=>{});
    }else if(provider==='director'&&director){
      void this.request(`${director}/matchmaking/tickets/${encodeURIComponent(ticket.id)}`,{
        method:'DELETE',
      }).catch(()=>{});
    }
  }
}
